import { IssueRow, PaginatedIssues } from '@components/pages/service-pages/issues/models/issue.model';
import { IssueService } from '@components/pages/service-pages/issues/services/issue.service';
import { PaginationComponent } from '@components/shared/pagination/pagination.component';
import { PaginationEvent } from '@models/pagination.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HardCodedConfigService } from '@services/util/hard-coded-config.service';
import { LogService } from '@services/util/log.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { BehaviorSubject, debounceTime, distinctUntilChanged, finalize, Observable, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
///////////////////////////////////////
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

/**
 * Interface for table sort events
 */
interface SortEvent {
  field: string;
  order: number;
  data?: IssueRow[];
}

@Component({
  selector: 'app-flat-type-table',
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    PaginatorModule,
    TagModule,
    MessageModule,
    TranslateModule,
    ConfirmDialogModule,
    ToastModule,
    PaginationComponent,
    InputTextModule,
    ReactiveFormsModule,
    InputGroupModule,
    InputGroupAddonModule
  ],
  templateUrl: './flat-type-table.component.html',
  providers: [ConfirmationService, MessageService],
  styleUrl: './flat-type-table.component.scss'
})
export class FlatTypeTableComponent implements OnInit, OnDestroy {
  // Default page size - matches API default of 30
  readonly pageSize = 30;

  // Current page (1-based)
  currentPage = 1;

  // Paginator first record (0-based) - required for PrimeNG paginator to track active page
  first = 0;

  // Search control with debounce
  searchControl = new FormControl('');

  // Issues data with pagination details
  paginatedIssues: PaginatedIssues | null = null;

  // Loading state
  loading = false;

  // Current search term
  searchTerm = '';

  // Current sort field and order
  sortField: string | null = null;
  sortOrder: 'asc' | 'desc' | null = null;

  // Keeps track of which column is sorted and in what direction
  sortState: { field: string | null; direction: 'asc' | 'desc' | null } = {
    field: null,
    direction: null
  };

  // Error state
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  readonly error$: Observable<string | null> = this.errorSubject.asObservable();

  // Component cleanup
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly issueService: IssueService,
    private readonly log: LogService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly hardCodedConfig: HardCodedConfigService,
    private readonly translateService: TranslateService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    // Setup search debounce
    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(this.hardCodedConfig.SEARCH_DEBOUNCE_TIME_MS), distinctUntilChanged())
      .subscribe((value) => {
        this.searchTerm = value || '';
        this.currentPage = 1; // Reset to first page when searching
        this.loadIssues();
      });

    this.loadIssues();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load issues for the current page with optional search and sort
   */
  loadIssues(): void {
    this.loading = true;
    this.errorSubject.next(null);

    // Prepare order parameter
    const orderField = this.sortField;
    const orderDirection = this.sortOrder;

    this.issueService
      .getIssues(this.currentPage, this.pageSize, this.searchTerm, orderField, orderDirection)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (data: PaginatedIssues): void => {
          this.paginatedIssues = data;
          this.log.debug('Loaded issues:', data);
        },
        error: (error: unknown): void => {
          this.errorSubject.next('Failed to load issues');
          this.log.error('Error loading issues:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('issues.loadError')
          });
        }
      });
  }

  /**
   * Handle pagination event from shared pagination component
   */
  onPaginationChange(event: PaginationEvent): void {
    this.currentPage = event.page;
    this.first = (event.page - 1) * event.pageSize;
    this.loadIssues();
  }

  /**
   * Clear the search input and reload issues
   */
  clearSearch(): void {
    this.searchControl.setValue('');
    // The subscription in ngOnInit will handle reloading issues
  }

  /**
   * Calculate total number of pages based on total records and page size
   */
  getTotalPages(): number {
    return Math.ceil((this.paginatedIssues?.totalCount ?? 0) / this.pageSize);
  }

  /**
   * Custom sort handler for p-table
   * @param event Sort event from PrimeNG
   */
  onSort(event: SortEvent): IssueRow[] {
    const clickedField = event.field;

    // If clicking on same column, toggle sort direction: none -> asc -> desc -> none
    if (clickedField === this.sortState.field) {
      // Toggle direction: null -> asc -> desc -> null
      if (this.sortState.direction === null) {
        this.sortState.direction = 'asc';
      } else if (this.sortState.direction === 'asc') {
        this.sortState.direction = 'desc';
      } else {
        // Reset sort completely when going beyond desc
        this.sortState.field = null;
        this.sortState.direction = null;
      }
    } else {
      // New column, start with ascending
      this.sortState.field = clickedField;
      this.sortState.direction = 'asc';
    }

    // Map UI sort fields to API sort fields
    const fieldMapping: Record<string, string> = {
      flat: 'flat.id',
      status: 'issueStatus.id'
    };

    // Set the API sort parameters
    this.sortField = this.sortState.field ? fieldMapping[this.sortState.field] || null : null;
    this.sortOrder = this.sortState.direction;

    this.log.debug('Custom sort:', {
      clickedField,
      sortState: this.sortState,
      apiField: this.sortField,
      apiOrder: this.sortOrder
    });

    // Manually sort the data by setting the correct sort parameters
    // and then re-fetch from the API
    this.loadIssues();

    // For customSort, we need to return the sorted data
    return [...(event.data || [])];
  }

  /**
   * Handle edit button click - navigate to issue detail page
   */
  onEdit(issue: IssueRow): void {
    this.router.navigate(['/issues', issue.id]);
  }

  /**
   * Handle delete button click
   */
  onDelete(issue: IssueRow): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('issues.delete.confirmMessage'),
      header: this.translateService.instant('common.delete.confirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.yesDelete'),
      rejectLabel: this.translateService.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.loading = true;

        this.issueService
          .deleteIssue(issue.id)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.loading = false))
          )
          .subscribe({
            next: (success: boolean): void => {
              if (success) {
                this.messageService.add({
                  severity: 'success',
                  summary: this.translateService.instant('common.success'),
                  detail: this.translateService.instant('issues.delete.successMessage')
                });
                this.loadIssues(); // Reload the list
              } else {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translateService.instant('common.error'),
                  detail: this.translateService.instant('issues.delete.errorMessage')
                });
              }
            },
            error: (error: unknown): void => {
              this.log.error('Error deleting issue:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.error'),
                detail: this.translateService.instant('issues.delete.errorMessage')
              });
            }
          });
      }
    });
  }
}
